import { cva } from 'class-variance-authority'

/**
 * The text-field box, separate from the component so Field.tsx exports only a
 * component — which is what Fast Refresh needs — and so a screen that has to
 * build a non-input control (a date range, a read-only well) can wear the same
 * skin without faking an `<input>`.
 *
 * The box is a wrapper, not the `<input>` itself, because a field with an
 * adornment has two focusable children and only one of them is the input. That
 * is why the focus ring is `has-[:focus-visible]` rather than `focus-visible`:
 * the ring belongs to the whole control, but it must still fire on the
 * focus-VISIBLE heuristic — `focus-within` would also paint it when the caller
 * moves focus programmatically after a failed submit.
 *
 * Every shape carries a border, so turning a field red on error does not move
 * the page by two pixels.
 *
 * The base is the whole field: one square box in the inset grey, 44px tall
 * (Figma 42, rounded up on the 4-grid because it is an input). Since the
 * 2026-10-05 motion pass it draws a 1px `border-input` edge, as the reference
 * mock does, and focus is a quiet border-colour change over the 150ms colour
 * duration. The 1px ring beside it keeps the indicator visible for people who
 * cannot tell the border shift apart, and is drawn on `:focus-visible` only. `shape` only keeps
 * old call sites compiling. `pill` renders the base. The others keep the look their screens were
 * built around, because each has to move with something outside this file: `boxed` with its
 * callers' own fill, and the three auth shapes with each other, since sign in and sign up are two
 * tabs of one page.
 */
export const fieldVariants = cva(
  'flex h-11 w-full items-center gap-2 rounded-sm border border-input bg-muted px-4 transition-[color,background-color,border-color,box-shadow] has-[:focus-visible]:border-ring has-[:focus-visible]:ring-1 has-[:focus-visible]:ring-ring has-[:disabled]:opacity-38',
  {
    variants: {
      shape: {
        /**
         * @deprecated V2 has no pill field, so this renders the base. Delete
         * the prop.
         */
        pill: '',
        /**
         * @deprecated The pre-refresh in-app field: the base plus a visible
         * hairline. AddTransactionModal pairs it with a `bg-card` fill, and on
         * a card-coloured panel that hairline is the box's only edge. Delete
         * the prop and that fill together.
         */
        boxed: 'border-border',
        /**
         * @deprecated The pre-refresh forgot-password and verify field, taller
         * than the base (its 50px → h-13 on the 4-grid). Delete the prop.
         */
        auth: 'h-13',
        /**
         * @deprecated The pre-refresh sign-up field. PhoneField's +977 chip is
         * drawn at its height, so the chip moves to the base with it.
         */
        authFilled: 'h-14 border-border-subtle bg-popover',
        /**
         * @deprecated The pre-refresh sign-in field, outlined on the page.
         * Delete the prop, on the same day as sign up's.
         */
        authOutlined: 'h-14 border-border-subtle bg-background',
        /**
         * rest:ub — the sign-in / sign-up / reset field (COMPARE-au §2): 48 high, the mock's 12px
         * radius, the card fill, and focus = a gold edge with a 3px accent halo at 22% (`--ring-halo`)
         * in place of the 1px ring. Hover changes nothing, as the mock's does not.
         */
        round:
          'h-12 rounded-auth border-border bg-card has-[:focus-visible]:border-accent has-[:focus-visible]:ring-0 has-[:focus-visible]:shadow-[var(--ring-halo)]',
      },
      invalid: {
        // The border is already destructive, so a bare 1px ring would change nothing on focus:
        // an offset outline in the same tone makes the indicator as plain as the green one.
        true: 'border-destructive has-[:focus-visible]:border-destructive has-[:focus-visible]:ring-destructive has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-destructive',
        false: '',
      },
    },
    defaultVariants: { invalid: false },
  },
)
